import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, User, Mail, Phone, Shield, Key, AlertCircle, AlertTriangle } from 'lucide-react';
import CustomSelect from '../shared/CustomSelect';
import { roleService } from '../../services/roleService';

const SystemUserFormPage = ({ user, onBack, onSubmit, loading, error }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        roleName: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [roles, setRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(true);

    // Fetch roles on mount
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                setRolesLoading(true);
                const response = await roleService.getAll();
                if (response.success && response.data) {
                    const filtered = response.data.filter(r => r.name !== 'SUPERADMIN');
                    setRoles(filtered);
                }
            } catch (err) {
                console.error('Error fetching roles:', err);
            } finally {
                setRolesLoading(false);
            }
        };
        fetchRoles();
    }, []);

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                phoneNumber: user.phoneNumber || '',
                username: user.username || '',
                roleName: user.roleName || user.role || '',
                password: '', // Don't populate password when editing
                confirmPassword: '',
                mustResetPassword: user.mustResetPassword || false
            });
        }
    }, [user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error when field is edited
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));
        validateField(field);
    };

    const validateField = (field) => {
        let error = '';

        switch (field) {
            case 'firstName':
                if (!formData.firstName.trim()) {
                    error = 'First name is required';
                } else if (formData.firstName.length < 2) {
                    error = 'First name must be at least 2 characters';
                }
                break;

            case 'lastName':
                if (!formData.lastName.trim()) {
                    error = 'Last name is required';
                } else if (formData.lastName.length < 2) {
                    error = 'Last name must be at least 2 characters';
                }
                break;

            case 'email':
                if (!formData.email.trim()) {
                    error = 'Email is required';
                } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
                    error = 'Invalid email address';
                }
                break;

            case 'password':
                if (formData.password && formData.password.length < 8) {
                    error = 'Password must be at least 8 characters';
                } else if (formData.password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
                    error = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
                }
                break;

            case 'confirmPassword':
                if (formData.password && formData.password !== formData.confirmPassword) {
                    error = 'Passwords do not match';
                }
                break;

            case 'phoneNumber':
                if (formData.phoneNumber && !/^[\d\s\-\+\(\)]+$/.test(formData.phoneNumber)) {
                    error = 'Invalid phone number format';
                }
                break;

            case 'roleName':
                if (!formData.roleName) {
                    error = 'Role is required';
                }
                break;

            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [field]: error
        }));

        return error === '';
    };

    const validateForm = () => {
        const fields = ['firstName', 'lastName', 'email', 'roleName'];

        // Password fields only needed when editing and password is provided
        if (user && formData.password) {
            fields.push('password', 'confirmPassword');
        }

        let isValid = true;

        fields.forEach(field => {
            const fieldValid = validateField(field);
            if (!fieldValid) {
                isValid = false;
            }
        });

        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Mark all required fields as touched
        const requiredFields = ['firstName', 'lastName', 'email', 'roleName'];
        if (user && formData.password) {
            requiredFields.push('password', 'confirmPassword');
        }

        const newTouched = {};
        requiredFields.forEach(field => {
            newTouched[field] = true;
        });
        setTouched(newTouched);

        if (validateForm()) {
            // Prepare data for submission - only include fields the backend expects
            const submitData = {
                email: formData.email,
                firstName: formData.firstName,
                lastName: formData.lastName,
                roleName: formData.roleName,
                phoneNumber: formData.phoneNumber || null // Backend accepts null for optional phoneNumber
            };

            // Only include password for new users or if changing password
            if (!user || formData.password) {
                submitData.password = formData.password;
            }

            // Log the final data being submitted
            console.log('Final submission data:', JSON.stringify(submitData, null, 2));

            onSubmit(submitData);
        }
    };

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={onBack}
                        className="p-2 hover:bg-gray-100 rounded-lg transition"
                        disabled={loading}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-900">
                            {user ? 'Edit User' : 'Add New User'}
                        </h2>
                        <p className="text-gray-600 mt-1">
                            {user ? 'Update system user information' : 'Create a new system user account'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6">
                {/* Show error message if there's a permission or other error */}
                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-red-900">Error Creating User</p>
                                <p className="text-sm text-red-700 mt-1">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Personal Information */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Personal Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                onBlur={() => handleBlur('firstName')}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    touched.firstName && errors.firstName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Enter first name"
                                disabled={loading}
                            />
                            {touched.firstName && errors.firstName && (
                                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                onBlur={() => handleBlur('lastName')}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                    touched.lastName && errors.lastName ? 'border-red-500' : 'border-gray-300'
                                }`}
                                placeholder="Enter last name"
                                disabled={loading}
                            />
                            {touched.lastName && errors.lastName && (
                                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Email Address <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('email')}
                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="user@example.com"
                                    disabled={loading}
                                />
                            </div>
                            {touched.email && errors.email && (
                                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('phoneNumber')}
                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        touched.phoneNumber && errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="+254 700 123456"
                                    disabled={loading}
                                />
                            </div>
                            {touched.phoneNumber && errors.phoneNumber && (
                                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Account Information */}
                <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        Account Information
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Role <span className="text-red-500">*</span>
                            </label>
                            {rolesLoading ? (
                                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-400 text-sm">
                                    Loading roles...
                                </div>
                            ) : (
                                <CustomSelect
                                    value={formData.roleName}
                                    onChange={(val) => {
                                        setFormData(prev => ({ ...prev, roleName: val }));
                                        if (errors.roleName) {
                                            setErrors(prev => ({ ...prev, roleName: '' }));
                                        }
                                    }}
                                    options={roles.map(r => ({
                                        value: r.name,
                                        label: r.label || r.name.replace(/_/g, ' '),
                                        description: r.description
                                    }))}
                                    placeholder="Select role"
                                    disabled={loading}
                                />
                            )}
                            {touched.roleName && errors.roleName && (
                                <p className="mt-1 text-sm text-red-600">{errors.roleName}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Password Section */}
                {user ? (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Key className="w-5 h-5" />
                            Change Password (Optional)
                        </h3>

                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-blue-800">
                                    Leave password fields blank to keep the current password unchanged.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('password')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Enter new password (optional)"
                                    disabled={loading}
                                />
                                {touched.password && errors.password && (
                                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm Password {formData.password && <span className="text-red-500">*</span>}
                                </label>
                                <input
                                    type="password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    onBlur={() => handleBlur('confirmPassword')}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                        touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                    placeholder="Confirm password"
                                    disabled={loading}
                                />
                                {touched.confirmPassword && errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Key className="w-5 h-5" />
                            Password
                        </h3>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-blue-800">
                                    A temporary password will be generated and emailed to the user. They will be required to change it on first login.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form Actions */}
                <div className="flex justify-end gap-3 pt-6 border-t">
                    <button
                        type="button"
                        onClick={onBack}
                        className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4" />
                                {user ? 'Update User' : 'Create User'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default SystemUserFormPage;
